import { ITEMS_PER_PAGE } from "~/constants";
import {
  ASSIGNEE,
  ASSET_NAME,
  CONNECTION_NAME,
  DATE_RANGE,
  FACILITY_NAME,
  SCHEDULED,
  TEAMS,
  TICKET_STATUS,
  TICKET_TYPE,
} from "~/constants/filters/filters";
import { isCustomFieldFilter } from "~/utils/filter/customFieldFilterValue";

type ActiveFilters = Record<string, any>;

const pickValues = (activeFilters: ActiveFilters, key: string): any[] =>
  Array.isArray(activeFilters?.[key]) && activeFilters[key].length
    ? activeFilters[key]
    : [];

/**
 * Built-in (non custom-field) filter keys handled explicitly below. Any other
 * key in `activeFilters` is a custom additional field, keyed by its Mongo id.
 * `DATE_RANGE`/`SCHEDULED` are work-order-only and unsupported here, but may
 * still be present in persisted state, so they are excluded too.
 */
const BUILT_IN_FILTER_KEYS = new Set<string>([
  TICKET_STATUS,
  TICKET_TYPE,
  ASSIGNEE,
  TEAMS,
  ASSET_NAME,
  CONNECTION_NAME,
  FACILITY_NAME,
  DATE_RANGE,
  SCHEDULED,
]);

interface CustomFieldCondition {
  fieldId: string;
  value: any;
}

/**
 * Collects active custom-field filters into the `customFields` shape the
 * backend expects (mirrors the work-order builder): `[{ fieldId, value }]`,
 * where `value` is the raw active-filter value written by the shared filter
 * dropdowns:
 *   - select  → array of option strings (matched with `$in`)
 *   - number  → raw string (equality)
 *   - text    → `{ value, filterVariant: "contains" | "exactly_matches" }`
 *               (the discriminator key is `filterVariant`, which is what the
 *               shared backend builder reads.)
 */
const pickCustomFields = (
  activeFilters: ActiveFilters,
): CustomFieldCondition[] => {
  const customFields: CustomFieldCondition[] = [];
  for (const key of Object.keys(activeFilters || {})) {
    if (BUILT_IN_FILTER_KEYS.has(key)) continue;
    const value = activeFilters[key];
    if (isCustomFieldFilter(key, value)) {
      customFields.push({ fieldId: key, value });
    }
  }
  return customFields;
};

/**
 * Maps the shared ticket filter scope (`SUPPORT_TICKET`) onto the
 * `listOwnOemSupportTickets` `where` shape documented by the backend.
 *
 * The Support Ticket backend accepts `status`/`type`/`asset`/`company` as
 * `ID | [ID]` (arrays mapped to `$in`) and `assignees`/`teams` as `[ID]`.
 * Custom additional fields (type "supportTickets") are forwarded as
 * `customFields: [{ fieldId, value }]`. Work-Order-only filters (date range,
 * scheduled) have no support-ticket equivalent and are dropped.
 */
export const generateSupportTicketQueryWhereCond = (
  activeFilters: ActiveFilters = {},
): Record<string, unknown> => {
  const status = pickValues(activeFilters, TICKET_STATUS);
  const type = pickValues(activeFilters, TICKET_TYPE);
  const assignees = pickValues(activeFilters, ASSIGNEE);
  const teams = pickValues(activeFilters, TEAMS);
  const asset = pickValues(activeFilters, ASSET_NAME);
  const company = [
    ...pickValues(activeFilters, CONNECTION_NAME),
    ...pickValues(activeFilters, FACILITY_NAME),
  ];
  const customFields = pickCustomFields(activeFilters);

  return {
    ...(status.length ? { status } : {}),
    ...(type.length ? { type } : {}),
    ...(assignees.length ? { assignees } : {}),
    ...(teams.length ? { teams } : {}),
    ...(asset.length ? { asset } : {}),
    ...(company.length ? { company } : {}),
    ...(customFields.length ? { customFields } : {}),
  };
};

interface SupportTicketVariablesArgs {
  activeFilters?: ActiveFilters;
  where?: Record<string, unknown>;
  searchQuery?: string;
  limit?: number;
  skip?: number;
  sort?: string[];
}

export const generateListOwnOemSupportTicketsVariables = ({
  activeFilters,
  where,
  searchQuery,
  limit = ITEMS_PER_PAGE,
  skip = 0,
  sort = [],
}: SupportTicketVariablesArgs) => {
  const filterConditions = generateSupportTicketQueryWhereCond(activeFilters);

  return {
    params: {
      limit,
      skip,
      sort,
      where: {
        ...filterConditions,
        ...(searchQuery ? { searchQuery } : {}),
        ...(where || {}),
      },
    },
  };
};
